import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { useLocation, useSearchParams } from "wouter";
import useMusioApi from "@/contexts/client/musio-client-context";
import { LoadingIndicator } from "@/components/ui/loading-indicator";
import { Checkbox } from "@/components/ui/checkbox";
import { ReactComponent as Logo } from "@/assets/musio-logos/logo_center_top.svg";
import { NotificationRow } from "@/components/ui/notification-row";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";

const ONBOARDING_OFFER_QUERY_PARAM = "offer";

export default function SignupPage() {
  const [_, navigate] = useLocation();
  const { musioApi } = useMusioApi();
  const [params] = useSearchParams();
  const onboardingOffer = params.get(ONBOARDING_OFFER_QUERY_PARAM);

  const [error, setError] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [contactName, setContactName] = useState("");
  const [updateBox, setUpdateBox] = useState(true);
  const [agreeBox, setAgreeBox] = useState(false);

  const { mutate, isPending } = musioApi.useMutation("post", "/signup", {
    onError: (error) => {
      console.log(error);
      setError("Password does not meet security requirements. Please try something else");
    },
    onSuccess: () => {
      navigate('/confirm-email');
    }
  });

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const err = validate();
    if (err != null) {
      console.log("signup error", err);
      setError(err);
      return;
    }

    mutate({
      body: {
        authId: email,
        password: password,
        contactName: contactName,
        emailMarketingConsent: updateBox,
        onboardingOffer: onboardingOffer ? onboardingOffer : undefined,
      },
    });
  }

  const validate = (): string | null => {
    if (contactName.length < 8) return "Contact Name must be at least 8 characters long";

    if (contactName.length > 64) return "Contact Name cannot be more than 64 characters long";

    if (password.length < 8) return "Password must be at least 8 characters long";

    if (password.length > 64) return "Password cannot be more than 64 characters long";

    if (password.length != confirmPassword.length) return "Confirmation password must match!";

    if (!agreeBox) return "Please review and accept the Terms of Service and Privacy Policy to proceed";

    return null
  }

  const handleReturn = (e: React.FormEvent) => {
    e.preventDefault();
    navigate("/login");
  };

  return (
    <ScrollArea className="h-full">
      <div className="flex flex-col overflow-auto items-center justify-center font-inter space-y-2">
        <div className="hidden w-[128px] h-[128px] sm:flex justify-center items-center">
          <Logo />
        </div>
        <form onSubmit={handleSignup}>
          <Card className="flex space-y-4 items-center justify-center flex-col musio-glass h-full w-full max-w-[478px]">
            <CardHeader className="w-full">
              <CardTitle className="font-semibold text-3xl text-center font-inter">Create Your Musio Account</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 w-full">
              {error ?
                <NotificationRow intent="error">
                  {error}
                </NotificationRow> : null}
              <div className="space-y-2">
                <Input
                  typeof="text"
                  type="text"
                  id="contactName"
                  placeholder="Name."
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  className="bg-musio-bg-light border-musio-bg-dark"
                />
                <Input
                  typeof="text"
                  type="email"
                  id="email"
                  placeholder="Email."
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-musio-bg-light border-musio-bg-dark"
                />
                <Input
                  typeof="text"
                  type="password"
                  id="password"
                  placeholder="Password."
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-musio-bg-light border-musio-bg-dark"
                />
                <Input
                  typeof="text"
                  type="password"
                  id="confirmEmail"
                  placeholder="Confirm Password."
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-musio-bg-light border-musio-bg-dark"
                />
              </div>
              <div className="space-y-6">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="notifications-updates"
                    defaultChecked
                    checked={updateBox}
                    onCheckedChange={(checked: boolean) => setUpdateBox(checked)}
                    className="cursor-pointer"
                  />
                  <label
                    htmlFor="notifications-updates"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    style={{ color: "var(--musio-text-light)", opacity: "50%" }}
                  >
                    I'd like news about updates and instruments releases
                  </label>
                </div>
                <div className="flex items-center space-x-1">
                  <Checkbox
                    id="terms-of-service"
                    defaultChecked
                    checked={agreeBox}
                    onCheckedChange={(checked: boolean) => setAgreeBox(checked)}
                    className="cursor-pointer"
                  />
                  <label
                    className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    style={{ color: "var(--musio-text-light)", opacity: "50%" }}
                  >
                    I agree with the Terms of Service & Privacy Policy
                  </label>
                </div>
              </div>
            </CardContent>
            <CardFooter className="space-y-2 w-full py-4">
              {isPending ? (
                <LoadingIndicator />
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 w-full">
                  <Button
                    className="w-full font-bold h-[35px] bg-musio-green text-musio-mid-gray"
                    type="submit">
                    Create
                  </Button>
                  <Button
                    className="w-full border-primary bg-musio-mid-gray font-bold text-musio-lilac border-none"
                    variant="outline"
                    onClick={(e) => handleReturn(e)}>
                    Login
                  </Button>
                </div>
              )}
            </CardFooter>
          </Card>
        </form>
      </div>
      <ScrollBar orientation="vertical" />
    </ScrollArea>
  )
}
